import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';

import { Alert } from '@app/ui/alert';

import { MATCHING_POLL_INTERVAL_MS, MATCHING_ROUTE, MATCHING_STALL_NOTICE_MS } from '../constants';
import { MATCHING_RUN_GET_ONE } from '../gql';
import { matchingSession } from '../session';
import { MatchingResultPayload, MatchingRun, MatchingRunScreenState, MatchingRunStatus } from '../types';
import { MatchingResultsScreen } from './MatchingResultsScreen';

interface MatchingRunScreenProps {
  readonly runId: string;
}

// `runId` arrives as a prop because the no-run redirect is the PAGE's job (see `matching-run.tsx`).
export const MatchingRunScreen = ({ runId }: MatchingRunScreenProps) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const [screenState, setScreenState] = useState<MatchingRunScreenState>(MatchingRunScreenState.running);
  const [run, setRun] = useState<MatchingRun | null>(null);
  const [stalled, setStalled] = useState(false);

  const { data: pollData, stopPolling } = useQuery<{ matchingRunGetOne: MatchingRun | null }>(MATCHING_RUN_GET_ONE, {
    variables: { runId },
    skip: !runId || screenState !== MatchingRunScreenState.running,
    pollInterval: MATCHING_POLL_INTERVAL_MS,
    fetchPolicy: 'network-only',
  });

  // `skip` derives from `screenState`, never from "stopPolling was already called" — a re-render
  // restarts a query whose `skip` is false (measured trap, `RuleTestPanel.spec.tsx:294`).
  useEffect(() => {
    if (pollData === undefined) {
      return;
    }

    const row = pollData.matchingRunGetOne;

    if (row === null) {
      // Terminal, not "still loading" — the only reachable cause is 02's 24h sweep (G-b).
      stopPolling();
      setScreenState(MatchingRunScreenState.failed);
    } else if (row.status === MatchingRunStatus.completed) {
      stopPolling();
      setRun(row);
      setScreenState(MatchingRunScreenState.results);
    } else if (row.status === MatchingRunStatus.failed) {
      stopPolling();
      setScreenState(MatchingRunScreenState.failed);
    }
  }, [pollData, stopPolling]);

  useEffect(() => {
    if (screenState !== MatchingRunScreenState.running) {
      return undefined;
    }

    setStalled(false);
    const timer = setTimeout(() => setStalled(true), MATCHING_STALL_NOTICE_MS);

    return () => clearTimeout(timer);
  }, [screenState, runId]);

  const handleReturnToSelection = () => {
    matchingSession.runId = null;
    navigate(MATCHING_ROUTE);
  };

  if (screenState === MatchingRunScreenState.failed) {
    return (
      <Alert severity="error">
        {t('matching.run.failedAlert')}
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" color="primary" onClick={handleReturnToSelection}>
            {t('matching.run.retryButton')}
          </Button>
        </Box>
      </Alert>
    );
  }

  if (screenState === MatchingRunScreenState.results) {
    return (
      <MatchingResultsScreen
        // `result` is `GraphQLJSON` on the wire (untyped) — narrowed to the local shape at this one
        // boundary; `buildResultsView` is defensively tolerant of a malformed blob either way.
        result={run?.result as MatchingResultPayload | null | undefined}
        runId={runId}
        onRunAgain={handleReturnToSelection}
      />
    );
  }

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'grey.500',
        borderRadius: 5,
        p: 5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2.25,
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      {/* 18px has no typography token (h3 is 20 at sm+) — design value, one-off (EVIDENCE §1.2). */}
      <Typography sx={{ fontSize: 18, fontWeight: 600 }}>{t('matching.run.runningTitle')}</Typography>
      {/* 13px has no typography token (body1 14, caption 12) — design value, one-off. */}
      <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t('matching.run.runningMessage')}</Typography>

      <Box
        aria-hidden
        sx={{
          width: '100%',
          maxWidth: 420,
          height: 6,
          borderRadius: 999,
          // Design literal #EDEEF1 — no exact token; nearest is grey.400 (#EFF2F4), imperceptible
          // behind the animated fill (same deviation 03 took for #FCF7FB → primaryLight.light).
          bgcolor: 'grey.400',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: '32%',
            height: 6,
            borderRadius: 999,
            bgcolor: 'primary.main',
            animation: 'iscSweep 1.1s ease-in-out infinite',
            '@keyframes iscSweep': {
              '0%': { transform: 'translateX(-100%)' },
              '100%': { transform: 'translateX(320%)' },
            },
          }}
        />
      </Box>

      <Typography variant="caption" color="text.secondary">
        {t('matching.run.runningScope', { count: matchingSession.selectedQuestionnaireIds.length })}
      </Typography>

      {stalled && (
        <Alert severity="warning" sx={{ width: '100%' }}>
          {t('matching.run.stalledNotice')}
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" color="primary" onClick={handleReturnToSelection}>
              {t('matching.run.backToSelectionButton')}
            </Button>
          </Box>
        </Alert>
      )}
    </Box>
  );
};
